import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  Fade,
} from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import toast from 'react-hot-toast';

// WhatsApp SVG icon component (official branding)
const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Browser/Globe icon
const BrowserIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const SESSION_STORAGE_KEY = 'sog_channel_choice';

interface ChannelChoice {
  vendorId: string;
  qrCode: string;
  choice: 'whatsapp' | 'browser';
  timestamp: number;
}

const ChannelChoicePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Extract params from URL
  const vendorId = searchParams.get('vendorId') || '';
  const qrCode = searchParams.get('qrCode') || searchParams.get('qr') || '';
  const vendorName = searchParams.get('vendorName') || 'Restaurant';
  const tableName = searchParams.get('tableName') || '';
  const vendorLogo = searchParams.get('vendorLogo') || '';
  const whatsappNumber = searchParams.get('whatsappNumber') || '';

  const [loading, setLoading] = useState(true);
  const [showFallbackLink, setShowFallbackLink] = useState(false);

  // Track whether user clicked WhatsApp and left the page
  const whatsappClickedRef = useRef(false);
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build the menu URL for browser ordering
  const buildMenuUrl = useCallback(() => {
    const params = new URLSearchParams({ qr: qrCode });
    return `/menu/${vendorId}?${params.toString()}`;
  }, [vendorId, qrCode]);

  // Check if customer has already made a channel choice for this vendor+qr
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const choice: ChannelChoice = JSON.parse(stored);
        // Check if choice is for same vendor and QR, and is less than 2 hours old
        const twoHours = 2 * 60 * 60 * 1000;
        if (
          choice.vendorId === vendorId &&
          choice.qrCode === qrCode &&
          Date.now() - choice.timestamp < twoHours
        ) {
          if (choice.choice === 'browser') {
            navigate(buildMenuUrl(), { replace: true });
            return;
          }
          // If they chose WhatsApp before, still show the page (they came back)
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [vendorId, qrCode, navigate, buildMenuUrl]);

  // Check WhatsApp webhook health on mount
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await api.get('/api/whatsapp/webhook/health');
        if (response.data?.status !== 'healthy' && response.data?.status !== 'ok') {
          // WhatsApp service is unhealthy, skip to menu
          toast('WhatsApp ordering is temporarily unavailable. Showing menu instead.', {
            icon: '\u2139\uFE0F',
            duration: 4000,
          });
          navigate(buildMenuUrl(), { replace: true });
          return;
        }
      } catch {
        // Health check failed -- WhatsApp service unreachable, skip to menu
        toast('WhatsApp ordering is temporarily unavailable. Showing menu instead.', {
          icon: '\u2139\uFE0F',
          duration: 4000,
        });
        navigate(buildMenuUrl(), { replace: true });
        return;
      }
      setLoading(false);
    };

    checkHealth();
  }, [navigate, buildMenuUrl]);

  // Page Visibility API: detect if user returns quickly after clicking WhatsApp
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && whatsappClickedRef.current) {
        // User left the page after clicking WhatsApp -- start a timer
        // If they come back within 5 seconds, show fallback link
      }

      if (document.visibilityState === 'visible' && whatsappClickedRef.current) {
        // User came back. Set a brief delay to confirm they actually returned
        // (not just a brief tab switch)
        visibilityTimerRef.current = setTimeout(() => {
          setShowFallbackLink(true);
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    };
  }, []);

  // Handle WhatsApp button click
  const handleWhatsAppClick = () => {
    whatsappClickedRef.current = true;

    // Store choice
    const choice: ChannelChoice = {
      vendorId,
      qrCode,
      choice: 'whatsapp',
      timestamp: Date.now(),
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(choice));

    // Build WhatsApp URL
    const message = encodeURIComponent(
      `Hi! I'd like to order from ${vendorName}. QR: ${qrCode}`
    );
    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '');
    const waUrl = `https://wa.me/${cleanNumber}?text=${message}`;

    window.open(waUrl, '_blank');

    // Start a 5-second timer. If user hasn't left the page by then, show fallback
    setTimeout(() => {
      if (document.visibilityState === 'visible') {
        setShowFallbackLink(true);
      }
    }, 5000);
  };

  // Handle Browser button click
  const handleBrowserClick = () => {
    // Store choice
    const choice: ChannelChoice = {
      vendorId,
      qrCode,
      choice: 'browser',
      timestamp: Date.now(),
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(choice));

    navigate(buildMenuUrl(), { replace: true });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.background,
        }}
      >
        <CircularProgress
          sx={{ color: theme.primary, mb: 2 }}
          size={40}
        />
        <Typography
          sx={{ color: theme.gray[500], fontSize: '0.875rem' }}
        >
          Checking availability...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="xs" sx={{ textAlign: 'center' }}>
        {/* Vendor Logo */}
        {vendorLogo && (
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={vendorLogo}
              alt={`${vendorName} logo`}
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${theme.components.card.border}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Vendor Name */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: theme.text,
            mb: 0.5,
            fontSize: '1.5rem',
            lineHeight: 1.3,
          }}
        >
          {vendorName}
        </Typography>

        {/* Table Name */}
        {tableName && (
          <Typography
            sx={{
              color: theme.gray[500],
              fontSize: '0.9rem',
              mb: 3,
            }}
          >
            Table: {tableName}
          </Typography>
        )}

        {!tableName && <Box sx={{ mb: 3 }} />}

        {/* Subtitle */}
        <Typography
          sx={{
            color: theme.gray[600],
            fontSize: '1rem',
            mb: 4,
            lineHeight: 1.5,
          }}
        >
          How would you like to place your order?
        </Typography>

        {/* WhatsApp Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleWhatsAppClick}
          sx={{
            backgroundColor: '#25D366', /* basil-todo */
            color: 'var(--text-inverse)',
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            px: 3,
            borderRadius: '12px',
            textTransform: 'none',
            mb: 2,
            boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            '&:hover': {
              backgroundColor: '#22c35e', /* basil-todo */
              boxShadow: '0 6px 20px rgba(37, 211, 102, 0.4)',
            },
            '&:active': {
              backgroundColor: 'var(--action)',
            },
          }}
        >
          <WhatsAppIcon size={22} />
          Order via WhatsApp
        </Button>

        {/* Browser Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleBrowserClick}
          sx={{
            backgroundColor: theme.button.primary.bg,
            color: theme.button.primary.text,
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            px: 3,
            borderRadius: '12px',
            textTransform: 'none',
            mb: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            '&:hover': {
              backgroundColor: theme.button.primary.hover,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          <BrowserIcon size={22} />
          Order in Browser
        </Button>

        {/* Fallback link -- shown when user comes back after clicking WhatsApp */}
        <Fade in={showFallbackLink} timeout={400}>
          <Box
            sx={{
              mt: 1,
              p: 2,
              backgroundColor: theme.components.card.background,
              border: `1px solid ${theme.components.card.border}`,
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{
                color: theme.gray[600],
                fontSize: '0.85rem',
                mb: 1,
              }}
            >
              Having trouble with WhatsApp?
            </Typography>
            <Typography
              component="a"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handleBrowserClick();
              }}
              sx={{
                color: theme.primary,
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  color: theme.button.primary.hover,
                },
              }}
            >
              Order here instead
            </Typography>
          </Box>
        </Fade>

        {/* Footer hint */}
        <Typography
          sx={{
            color: theme.gray[500],
            fontSize: '0.75rem',
            mt: 4,
            opacity: 0.7,
          }}
        >
          Powered by SOG
        </Typography>
      </Container>
    </Box>
  );
};

export default ChannelChoicePage;
